import React, { useMemo } from 'react';
import { Label, Card, CardBody, Row, Col } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';
import { toggleProps } from '../../../utils/a11y';
import { RespondMessageBubble } from '../ui';
import { COUNTER_ACTION_BTN_STYLE } from '../constants';
import AgreeLockButton from '../clause/AgreeLockButton';
import {
  getResponseSortTime,
  toLockedFlag,
  canAgreeLastCounter,
} from '../respond/helpers';
import { CounterDetailItem, CounterValueBlock } from '../payment';

const EMPTY_COUNTER_BADGE_STYLE = {
  backgroundColor: '#ADB5BD',
  color: '#fff',
};

const ValueField = ({ label, value }) => (
  <div className='border-start border-2 border-primary ps-3'>
    <div className='text-muted small fw-semibold mb-1'>{label}</div>
    <div className='text-dark fs-13 mb-0 lh-base'>{value || '-'}</div>
  </div>
);

const isSameCounterDisplay = (original, counter) =>
  String(original ?? '').trim() === String(counter ?? '').trim();

const CounterValueGrid = ({
  label,
  counterDisplay,
  showPartyType,
  partyTypeCounter,
  isValueSame,
  isPartyTypeSame,
}) => (
  <Row className='gy-4'>
    <Col lg={showPartyType ? 6 : 12}>
      <CounterDetailItem
        label={label}
        value={counterDisplay}
        unchanged={isValueSame}
      />
    </Col>
    {showPartyType && (
      <Col lg={6}>
        <CounterDetailItem
          label='Operation Tolerance Options'
          value={partyTypeCounter}
          unchanged={isPartyTypeSame}
        />
      </Col>
    )}
  </Row>
);

const CounterNegotiationCard = ({
  title,
  subtitle,
  partyType,
  partyTypeCounter,
  label,
  currentDisplay,
  counterDisplay,
  hasCounter: hasCounterProp,
  isExpanded,
  onToggleExpand,
  isEditing,
  editable,
  allowCounterEdit = false,
  isEditingCounter = false,
  isSaving,
  isSavingCounter = false,
  isDraftDirty,
  isCounterDraftDirty = false,
  onStartEdit,
  onCancel,
  onSave,
  onDelete,
  onStartCounterEdit,
  onCancelCounterEdit,
  onSaveCounter,
  onDeleteCounter,
  onRespond,
  historyItems = [],
  isEvaluatorView = false,
  extraFields = [],
  onLockResponse,
  isRespondClosed = false,
  proposeCounterLabel = 'Propose Counter Value',
  notice = null,
  counterEditor = null,
  // Agree & Lock per field (di bawah nilai counter); thread tanpa Agree.
  counterFooter = null,
  allowThreadAgree = true,
  children,
}) => {
  const hasCounter = hasCounterProp ?? !!(counterDisplay || partyTypeCounter);
  const sortedHistory = useMemo(
    () =>
      [...(historyItems ?? [])].sort(
        (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
      ),
    [historyItems]
  );
  const isThreadLocked = toLockedFlag(
    sortedHistory[sortedHistory.length - 1]?.isLocked
  );
  const canRespond =
    typeof onRespond === 'function' && !isThreadLocked && !isRespondClosed;
  const showPartyType = partyType !== undefined;
  const isValueSame = isSameCounterDisplay(currentDisplay, counterDisplay);
  const isPartyTypeSame = isSameCounterDisplay(partyType, partyTypeCounter);
  // Offer write: editable+isEditing. IC after evaluator respond: allowCounterEdit.
  const isCounterFormOpen = editable ? isEditing : Boolean(isEditingCounter);
  const canEditCounter =
    (editable || allowCounterEdit) &&
    !isEvaluatorView &&
    !isRespondClosed &&
    !isCounterFormOpen;
  const showCounterButton = canEditCounter && !hasCounter;
  const showCounterActions = canEditCounter && hasCounter;
  const startCounterEdit = editable ? onStartEdit : onStartCounterEdit;
  const cancelCounterEdit = editable ? onCancel : onCancelCounterEdit;
  const saveCounterEdit = editable ? onSave : onSaveCounter;
  const deleteCounterAction = editable ? onDelete : onDeleteCounter;
  const counterFormBusy = editable ? isSaving : isSavingCounter;
  const counterFormDirty = editable ? isDraftDirty : isCounterDraftDirty;
  const counterFormBody = editable ? children : counterEditor;

  const showHistory = !editable && isExpanded && sortedHistory.length > 0;
  const showRespondEditor =
    !editable && isExpanded && isEditing && !isThreadLocked && !isRespondClosed;
  const showCounterBlock = hasCounter && !isCounterFormOpen;
  const counterBlockTitle =
    editable || allowCounterEdit ? 'Proposed Counter Value' : 'Counter Value';

  return (
    <Card className='border shadow-sm mb-0 bg-white'>
      <CardBody>
        <div className='d-flex align-items-start justify-content-between gap-3 mb-3'>
          <div>
            <h5 className='fw-bold text-dark mb-1'>{title}</h5>
            {subtitle ? (
              <p className='text-muted small mb-0'>{subtitle}</p>
            ) : null}
          </div>
          {(showCounterButton || showCounterActions) && (
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              {showCounterButton && (
                <ActionButton
                  variant='secondary'
                  size='sm'
                  icon='ri-edit-line'
                  className='flex-shrink-0'
                  style={COUNTER_ACTION_BTN_STYLE}
                  onClick={() => {
                    if (!isExpanded && onToggleExpand) onToggleExpand();
                    startCounterEdit?.();
                  }}
                >
                  Counter
                </ActionButton>
              )}
              {showCounterActions && (
                <>
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-edit-line'
                    className='flex-shrink-0'
                    style={COUNTER_ACTION_BTN_STYLE}
                    onClick={() => startCounterEdit?.()}
                  >
                    Edit
                  </ActionButton>
                  {deleteCounterAction && (
                    <ActionButton
                      variant='destructive'
                      size='sm'
                      icon='ri-delete-bin-line'
                      confirm={false}
                      className='flex-shrink-0'
                      onClick={deleteCounterAction}
                    >
                      Delete
                    </ActionButton>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {notice}

        <Row className='gy-4 mb-3'>
          {extraFields.map(field => (
            <Col key={field.label} lg={4}>
              <ValueField label={field.label} value={field.value || '-'} />
            </Col>
          ))}
          <Col lg={showPartyType || extraFields.length > 0 ? 4 : 12}>
            <ValueField label={label} value={currentDisplay || '-'} />
          </Col>
          {showPartyType && (
            <Col lg={4}>
              <ValueField
                label='Operation Tolerance Options'
                value={partyType || '-'}
              />
            </Col>
          )}
        </Row>

        {showCounterBlock && (
          <CounterValueBlock title={counterBlockTitle}>
            <CounterValueGrid
              label={label}
              counterDisplay={counterDisplay}
              showPartyType={showPartyType}
              partyTypeCounter={partyTypeCounter}
              isValueSame={isValueSame}
              isPartyTypeSame={isPartyTypeSame}
            />
          </CounterValueBlock>
        )}

        {counterFooter}

        {(editable || allowCounterEdit) && isCounterFormOpen && (
          <div className='mt-2 p-3 bg-light rounded-3 border'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                {proposeCounterLabel}
              </Label>
              {counterFormDirty && (
                <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                  <i className='ri-error-warning-line fs-14'></i>
                  Unsaved Changes
                </span>
              )}
            </div>

            <div className='mb-3 bg-white p-2 border rounded-3'>
              {counterFormBody}
            </div>

            <div className='d-flex gap-2 justify-content-end'>
              <ActionButton
                variant='secondary'
                size='sm'
                onClick={cancelCounterEdit}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                onClick={saveCounterEdit}
                disabled={counterFormBusy}
              >
                {counterFormBusy ? 'Submitting...' : 'Submit Counter'}
              </ActionButton>
            </div>
          </div>
        )}

        <div className='border-top pt-3 mt-3 d-flex justify-content-between align-items-center'>
          <div
            className='d-flex align-items-center gap-2'
            style={{ cursor: 'pointer' }}
            {...toggleProps(onToggleExpand, isExpanded, undefined, 'expanded')}
          >
            <span
              className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                isExpanded
                  ? 'bg-primary-subtle text-primary'
                  : 'bg-light text-muted'
              }`}
              style={{ width: '28px', height: '28px' }}
            >
              <i
                className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                aria-hidden='true'
              ></i>
            </span>
            {sortedHistory.length > 0 ? (
              <>
                <span
                  className='bg-danger text-white rounded-pill d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                  style={{
                    minWidth: '20px',
                    height: '20px',
                    padding: sortedHistory.length > 9 ? '0 6px' : 0,
                  }}
                >
                  {sortedHistory.length}
                </span>
                <span className='fw-semibold text-dark fs-13'>Response</span>
              </>
            ) : editable && !hasCounter ? (
              <span
                className='rounded-pill px-2 py-1 fs-11 fw-semibold'
                style={EMPTY_COUNTER_BADGE_STYLE}
              >
                No counter proposals
              </span>
            ) : (
              <span className='text-muted fs-13'>No responses yet</span>
            )}
          </div>

          {canRespond && (
            <ActionButton
              variant='secondary'
              size='sm'
              icon='ri-chat-3-line'
              style={COUNTER_ACTION_BTN_STYLE}
              onClick={() => {
                if (!isExpanded && onToggleExpand) onToggleExpand();
                onRespond();
              }}
            >
              Respond
            </ActionButton>
          )}
        </div>

        {showHistory && (
          <div className='mt-3'>
            {sortedHistory.map((item, index) => {
              const evaluatorItem = item?.isPertamina === true;
              const metaDate = formatDateToddmmyyhhmm(
                item?.updatedDate || item?.createdDate
              );
              const authorLabel = evaluatorItem
                ? item.createdByLabel || 'PT Pertamina Patra Niaga'
                : item.createdByLabel || 'Counter';
              const lastItem = sortedHistory[sortedHistory.length - 1];
              const canManage =
                !isRespondClosed &&
                lastItem &&
                String(item.id) === String(lastItem.id) &&
                (isEvaluatorView ? evaluatorItem : !evaluatorItem) &&
                !item.isLocked;
              const isOwnEditing = canManage && isEditing;

              if (isOwnEditing) return null;

              return (
                <RespondMessageBubble
                  key={item.id ?? `${title}-${index}-${item.display}`}
                  isEvaluator={evaluatorItem}
                  content={item.display}
                  authorLabel={authorLabel}
                  metaDate={metaDate}
                  footer={
                    <>
                      {allowThreadAgree &&
                        canAgreeLastCounter(sortedHistory, item) &&
                        (!isRespondClosed || item.isLocked) && (
                          <div className='mt-3 pt-2 border-top'>
                            <AgreeLockButton
                              embedded
                              isLocked={item.isLocked}
                              onLock={
                                !isRespondClosed &&
                                isEvaluatorView &&
                                item.id &&
                                onLockResponse
                                  ? locked => onLockResponse(item.id, locked)
                                  : undefined
                              }
                              disabled={isSaving}
                            />
                          </div>
                        )}
                      {canManage &&
                      !item.isLocked &&
                      !isRespondClosed &&
                      (onStartEdit || onDelete) ? (
                        <div className='mt-3 pt-2 border-top d-flex align-items-center gap-2'>
                          {onStartEdit && (
                            <ActionButton
                              variant='secondary'
                              size='sm'
                              className='flex-grow-1'
                              onClick={onStartEdit}
                            >
                              Edit
                            </ActionButton>
                          )}
                          {onDelete && (
                            <ActionButton
                              variant='destructive'
                              size='sm'
                              confirm={false}
                              className='flex-grow-1'
                              onClick={onDelete}
                            >
                              Remove
                            </ActionButton>
                          )}
                        </div>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </div>
        )}

        {showRespondEditor && (
          <div className='mt-2'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                Respond Notes
              </Label>
              {isDraftDirty && (
                <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                  <i className='ri-error-warning-line fs-14'></i>
                  Unsaved Changes
                </span>
              )}
            </div>

            {children}

            <div className='d-flex gap-2 mt-3 justify-content-end'>
              <ActionButton
                variant='secondary'
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                onClick={onSave}
                disabled={isSaving || !isDraftDirty}
              >
                {isSaving ? 'Saving...' : 'Send Response'}
              </ActionButton>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CounterNegotiationCard;
